import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {TranslateService} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {
  ECaseDataTable,
  ECaseDateFormatterPipe,
  EcaseHttpService,
  ECaseSnackBarService,
  ECaseUtils,
  ExplorerService,
  LoginAuthenticationService,
  ParametersService
} from 'synto-common';
import {DocumentApprovalDialogComponent} from './document-approval-dialog/document-approval-dialog.component';
import {DocumentArchiveDialogComponent} from './document-archive-dialog/document-archive-dialog.component';
import {DefaultSort, DocumentItem, DocumentListRequest, DocumentListResponse, UIMetadata} from './document-list.models';
import {DocumentReplaceDialogComponent} from './document-replace-dialog/document-replace-dialog.component';

@Component({
  selector: 'app-synto-document-list',
  templateUrl: './synto-document-list.component.html',
  styleUrls: ['./synto-document-list.component.scss']
})
export class SyntoDocumentListComponent extends ECaseDataTable implements OnInit, OnChanges, OnDestroy {
  private static readonly LIVE_DOC_ID_TEAM_MEMBER_CONSENT = 999999999999000;
  private static readonly LIVE_DOC_ID_APPLICATION_REVISIONS_IN_REVIEW = 999999999999001;
  private static readonly LIVE_DOC_ID_APPLICATION_REVIEWS = 999999999999002;
  private static readonly LIVE_DOC_ID_APPLICATION_UNIQUE_REVIEWS = 999999999999003;
  private static readonly LIVE_DOC_ID_PRE_APPLICATION_REVIEWS = 999999999999004;
  private static readonly LIVE_DOC_ID_APPLICATION_REVISIONS = 999999999999005;
  private static readonly LIVE_DOC_ID_PA_REPORT = 999999999999006;

  private static readonly LIVE_DOCUMENT_TYPE_IDS: readonly number[] = [
    SyntoDocumentListComponent.LIVE_DOC_ID_TEAM_MEMBER_CONSENT,
    SyntoDocumentListComponent.LIVE_DOC_ID_APPLICATION_REVISIONS_IN_REVIEW,
    SyntoDocumentListComponent.LIVE_DOC_ID_APPLICATION_REVIEWS,
    SyntoDocumentListComponent.LIVE_DOC_ID_APPLICATION_UNIQUE_REVIEWS,
    SyntoDocumentListComponent.LIVE_DOC_ID_PRE_APPLICATION_REVIEWS,
    SyntoDocumentListComponent.LIVE_DOC_ID_APPLICATION_REVISIONS,
    SyntoDocumentListComponent.LIVE_DOC_ID_PA_REPORT
  ];

  @Input() screenName: string;
  @Input() entityType: string;
  @Input() entityId: number;
  @Input() isPublic: boolean = false;
  @Output() actionClicked = new EventEmitter<{ action: string; row: DocumentItem }>();

  uiMetadata: UIMetadata | null = null;
  documents: DocumentItem[] = [];
  totalCount = 0;
  loading = false;
  selectedLang: string;

  filterObject: { [key: string]: string } = {};
  showImage = true;

  computedDisplayedColumns: string[] = [];

  sortActive: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  pageSize = 10;
  pageSizeOptions = [10, 25, 50, 100];
  pageIndex = 0;

  private destroy$ = new Subject<void>();

  constructor(
    translate: TranslateService,
    parameterService: ParametersService,
    private eCaseHttpService: EcaseHttpService,
    private explorerService: ExplorerService,
    private eCaseSnackBarService: ECaseSnackBarService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private eCaseDateFormatterPipe: ECaseDateFormatterPipe,
    private loginAuthenticationService: LoginAuthenticationService
  ) {
    super(translate, parameterService);
    this.selectedLang = translate.getDefaultLang() || 'en';
  }

  ngOnInit(): void {
    this.setupLanguageSubscription();
    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource([]);
    }
    if (this.screenName && this.entityType && this.entityId != null) {
      this.loadDocuments();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const shouldReload =
      (changes.screenName && !changes.screenName.firstChange) ||
      (changes.entityType && !changes.entityType.firstChange) ||
      (changes.entityId && !changes.entityId.firstChange);

    if (shouldReload && this.screenName && this.entityType && this.entityId != null) {
      this.loadDocuments();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh(): void {
    this.loadDocuments();
  }

  private setupLanguageSubscription(): void {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectedLang = this.translate.currentLang || this.translate.getDefaultLang() || 'en';
        if (this.documents.length > 0) {
          this.renewDataSource(this.documents);
        }
      });
  }

  override renewDataSource(newDataArray: any[]): void {
    this.dataSource = new MatTableDataSource(newDataArray);
    if (this.sort.toArray()[0]) {
      this.dataSource.sort = this.sort.toArray()[0];
      this.dataSource.sortData = (data, _) => data;
    }
  }

  preProcessDocs(docs: DocumentItem[]): DocumentItem[] {
    return docs.map(row => {
      if (row.isDocumentApprovalRequired) {
        if (row.isDocumentApprovalWorkflowStarted) {
          row.isDocumentApprovalProcessed = row.isDocumentApprovalProcessed ? 1 : 0;
        } else {
          row.isDocumentApprovalProcessed = -2;
        }
      } else {
        row.isDocumentApprovalProcessed = -1;
      }
      return row;
    });
  }

  private loadDocuments(): void {
    if (!this.screenName || !this.entityType || this.entityId == null) {
      return;
    }

    this.loading = true;
    const request: DocumentListRequest = {
      screenName: (this.screenName || '').toUpperCase(),
      entityType: this.entityType,
      entityId: this.entityId,
      pageNumber: this.pageIndex,
      pageSize: this.pageSize
    };

    if (this.sortActive && typeof this.sortActive === 'string' && this.sortActive.trim() !== '') {
      const backendSortColumn = this.mapColumnNameToBackendSortable(this.sortActive.trim());
      if (backendSortColumn) {
        request.sortBy = backendSortColumn;
        request.sortDirection = this.sortDirection ? (this.sortDirection.toUpperCase() as 'ASC' | 'DESC') : undefined;
      }
    }

    const filters: { [key: string]: string } = {};
    if (this.filterObject) {
      Object.keys(this.filterObject).forEach(key => {
        const value = this.filterObject[key];
        if (value && value.trim() !== '') {
          filters[key] = value.trim();
        }
      });
    }
    if (Object.keys(filters).length > 0) {
      request.filters = filters;
    }

    this.explorerService.getDocumentList(request, this.isPublic)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.loading = false;

          if (response && response.status === false) {
            this.eCaseSnackBarService.show(
              'failure',
              ECaseUtils.getTranslatedValueFromKey(this.translate, response.errorMessage || 'ecase.explorer.documentList.configMissing')
            );
            this.resetData();
            this.cdr.detectChanges();
            return;
          }

          let responseData: DocumentListResponse;
          if (response?.response) {
            responseData = response.response;
          } else if (response?.data) {
            responseData = response.data;
          } else if (response?.ui && response?.documents) {
            responseData = response;
          } else {
            responseData = response as DocumentListResponse;
          }

          if (!responseData || !responseData.ui || !responseData.documents) {
            this.eCaseSnackBarService.show(
              'failure',
              ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.configMissing')
            );
            this.resetData();
            this.cdr.detectChanges();
            return;
          }

          const isFirstLoad = !this.uiMetadata;
          this.uiMetadata = responseData.ui;
          this.documents = responseData.documents.resultList || [];
          this.totalCount = responseData.documents.totalCount || 0;

          if (this.uiMetadata && this.uiMetadata.columns && this.uiMetadata.columns.includes('approvals')) {
            this.documents = this.preProcessDocs(this.documents);
          }

          this.setupColumns();
          this.setupFilters();

          this.showImage = !this.hasActiveFilters();

          if (isFirstLoad) {
            this.applyDefaultSort();
          }

          this.initializeDataSource();

          this.cdr.detectChanges();
        },
        (error: any) => {
          this.loading = false;
          const errorMessage = error?.errorMessage ||
            error?.error?.errorMessage ||
            error?.error?.message ||
            error?.message ||
            'ecase.explorer.documentList.loadError';
          this.eCaseSnackBarService.show(
            'failure',
            ECaseUtils.getTranslatedValueFromKey(this.translate, errorMessage)
          );
          this.resetData();
          this.cdr.detectChanges();
        }
      );
  }

  private resetData(): void {
    this.uiMetadata = null;
    this.documents = [];
    this.totalCount = 0;
    this.computedDisplayedColumns = [];
    this.filterObject = {};
    this.originalArray = [];
    this.serviceArray = [];
    this.showImage = true;
    this.sortActive = '';
    this.sortDirection = 'asc';
    this.pageIndex = 0;
    this.dataSource = new MatTableDataSource([]);
  }

  private setupColumns(): void {
    if (!this.uiMetadata || !this.uiMetadata.columns) {
      this.computedDisplayedColumns = [];
      return;
    }
    this.computedDisplayedColumns = [...this.uiMetadata.columns];
    this.computedDisplayedColumns.push('actions');
    this.displayedColumns = this.computedDisplayedColumns;
  }

  private setupFilters(): void {
    if (!this.uiMetadata || !this.uiMetadata.filteredColumns) {
      this.filterObject = {};
      return;
    }

    const filteredColumns = this.uiMetadata.filteredColumns;
    if (!this.filterObject) {
      this.filterObject = {};
    }

    filteredColumns.forEach(column => {
      if (!(column in this.filterObject)) {
        this.filterObject[column] = '';
      }
    });
  }

  private applyDefaultSort(): void {
    if (this.sortActive || !this.uiMetadata || !this.uiMetadata.defaultSort) {
      return;
    }

    const defaultSort: DefaultSort = this.uiMetadata.defaultSort;
    const sortColumn = defaultSort.column;
    const sortDirection: 'asc' | 'desc' = (defaultSort.direction || 'asc').toLowerCase() === 'desc' ? 'desc' : 'asc';

    this.sortActive = sortColumn;
    this.sortDirection = sortDirection;
  }

  private initializeDataSource(): void {
    if (!this.documents || !Array.isArray(this.documents)) {
      this.documents = [];
    }

    this.originalArray = [...this.documents];
    this.serviceArray = [...this.documents];

    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource([]);
    }

    this.renewDataSource(this.documents);
  }

  sortColumn(event: any): void {
    const newSortActive = event.active;
    const newSortDirection = event.direction || 'asc';

    if (newSortActive === 'actions') {
      return;
    }

    if (this.sortActive !== newSortActive || this.sortDirection !== newSortDirection) {
      this.sortActive = newSortActive;
      this.sortDirection = newSortDirection as 'asc' | 'desc';
      this.pageIndex = 0;
      this.loadDocuments();
    }
  }

  private mapColumnNameToBackendSortable(columnName: string): string | null {
    if (!columnName) {
      return null;
    }

    const columnMapping: { [key: string]: string } = {
      'documentName': 'fileName',
      'fileName': 'fileName',
      'description': 'description',
      'attachmentDate': 'attachmentDate',
      'attachedBy': 'attachedBy'
    };

    return columnMapping[columnName] || columnName;
  }

  getLocalizedValue(field: { [key: string]: string } | undefined): string {
    if (!field || typeof field !== 'object') {
      return '';
    }

    if (field[this.selectedLang]) {
      return field[this.selectedLang];
    }

    const defaultLang = this.translate.getDefaultLang() || 'en';
    if (field[defaultLang]) {
      return field[defaultLang];
    }

    const keys = Object.keys(field);
    if (keys.length > 0) {
      return field[keys[0]];
    }

    return '';
  }

  getColumnHeader(column: string): string {
    const columnKeyMap: { [key: string]: string } = {
      'actions': 'ecase.explorer.documentList.actions',
      'documentName': 'ecase.explorer.documentList.columnDocumentName',
      'documentCategory': 'ecase.explorer.documentList.columnDocumentCategory',
      'documentType': 'ecase.explorer.documentList.columnDocumentType',
      'formType': 'ecase.explorer.documentList.columnFormType',
      'fileName': 'ecase.explorer.documentList.columnFileName',
      'description': 'ecase.explorer.documentList.columnDescription',
      'attachmentDate': 'ecase.explorer.documentList.attachmentDateHeader',
      'attachedBy': 'ecase.explorer.documentList.columnAttachedBy',
      'actorRole': 'ecase.explorer.documentList.columnActorRole',
      'fileSize': 'ecase.explorer.documentList.columnFileSize',
      'approvals': 'ecase.explorer.documentList.columnApprovals',
      'authorizationPayment': 'ecase.explorer.documentList.columnAuthorizationPayment',
      'isArchived': 'ecase.explorer.documentList.columnIsArchived',
      'validityYear': 'ecase.explorer.documentList.columnValidityYear',
      'language': 'ecase.explorer.documentList.columnLanguage',
      'comment': 'ecase.explorer.documentList.columnComment'
    };
    const key = columnKeyMap[column];
    if (key) {
      return ECaseUtils.getTranslatedValueFromKey(this.translate, key);
    }
    return column
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  getCellValue(row: DocumentItem, column: string): string {
    if (column === 'actions') {
      return '';
    }

    const value = (row as any)[column];

    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      return this.getLocalizedValue(value as { [key: string]: string });
    }

    if (column === 'attachmentDate') {
      try {
        let date: Date;
        if (value instanceof Date) {
          date = value;
        } else if (typeof value === 'string') {
          date = new Date(value);
        } else {
          return String(value);
        }

        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
        }
      } catch (e) {
        return String(value);
      }
    }

    if (typeof value === 'string' && (value.includes('T') || value.includes('-'))) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString(this.selectedLang || 'en', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      } catch (e) {
      }
    }

    if (value instanceof Date) {
      try {
        return value.toLocaleDateString(this.selectedLang || 'en', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return String(value);
      }
    }

    if (column === 'fileSize' && typeof value === 'number') {
      if (value === -1) {
        return ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.fileSizeGenerated');
      }
      if (value === 0) {
        return ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.fileSizeNa');
      }
      const units = ['B', 'KB', 'MB', 'GB'];
      let size = value;
      let unitIndex = 0;
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      return `${size.toFixed(2)} ${units[unitIndex]}`;
    }

    if (column === 'isArchived' && typeof value === 'boolean') {
      return ECaseUtils.getTranslatedValueFromKey(this.translate,
        value ? 'ecase.explorer.documentList.yes' : 'ecase.explorer.documentList.no');
    }

    return String(value);
  }

  applyFilter(): void {
    this.pageIndex = 0;
    this.showImage = false;
    this.loadDocuments();
  }

  private hasActiveFilters(): boolean {
    if (!this.filterObject) {
      return false;
    }
    return Object.values(this.filterObject).some(value => value && value.trim() !== '');
  }

  clearFilter(): void {
    if (this.uiMetadata && this.uiMetadata.filteredColumns) {
      this.uiMetadata.filteredColumns.forEach(column => {
        this.filterObject[column] = '';
      });
    }
    this.showImage = true;
    this.pageIndex = 0;
    this.loadDocuments();
  }

  getActionLabel(action: string): string {
    if (!action) return '';
    const keyMap: { [key: string]: string } = {
      'edit': 'ecase.explorer.documentList.actionEdit',
      'replace': 'ecase.explorer.documentList.actionReplace',
      'archive': 'ecase.explorer.documentList.actionArchive',
      'upload': 'ecase.explorer.documentList.actionUpload'
    };
    const key = keyMap[action.toLowerCase()];
    if (key) {
      return ECaseUtils.getTranslatedValueFromKey(this.translate, key);
    }
    return action.charAt(0).toUpperCase() + action.slice(1).toLowerCase();
  }

  handleAction(row: DocumentItem, action: string): void {
    if (action === 'replace' || action.toLowerCase().includes('replace')) {
      if ((row as any).canReplace === true) {
        const dialogRef = this.dialog.open(DocumentReplaceDialogComponent, {
          width: '300px',
          data: {row}
        });
        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
          if (result) {
            this.loadDocuments();
          }
        });
      } else {
        this.actionClicked.emit({action, row});
      }
      return;
    }
    if (action === 'archive' || action.toLowerCase().includes('archive')) {
      const dialogRef = this.dialog.open(DocumentArchiveDialogComponent, {
        width: '600px',
        data: {row}
      });
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result) {
          this.loadDocuments();
        }
      });
    } else {
      this.actionClicked.emit({action, row});
    }
  }

  getFilterRowColumns(): string[] {
    const filterColumns: string[] = [];
    if (this.computedDisplayedColumns && this.computedDisplayedColumns.length > 0) {
      this.computedDisplayedColumns.forEach((_, index) => {
        filterColumns.push(`header-row-${index}-group`);
      });
    }
    return filterColumns;
  }

  onPaginationChange(event: any): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadDocuments();
  }

  isDownloadAvailable(row: DocumentItem): boolean {
    if (!row) {
      return false;
    }

    const documentTypeId = (row as any)?.documentTypeId;

    if (SyntoDocumentListComponent.LIVE_DOCUMENT_TYPE_IDS.includes(documentTypeId)) {
      return true;
    }

    if (row.fileSize === undefined || row.fileSize === null) {
      return false;
    }

    const fileSize = typeof row.fileSize === 'number' ? row.fileSize : Number(row.fileSize);

    if (isNaN(fileSize)) {
      return false;
    }

    return fileSize > 0 || fileSize === -1;
  }

  shouldShowActions(row: DocumentItem): boolean {
    return this.getVisibleActionsForRow(row).length > 0;
  }

  getVisibleActionsForRow(row: DocumentItem): string[] {
    if (!row || row.fileSize === -1 || !this.uiMetadata?.availableActions) {
      return [];
    }

    const availableActions = Array.isArray(this.uiMetadata.availableActions)
      ? this.uiMetadata.availableActions
      : Object.keys(this.uiMetadata.availableActions).filter(action => this.uiMetadata.availableActions[action] === true);

    return availableActions.filter(action => this.isActionVisible(row, action));
  }

  isActionVisible(row: DocumentItem, action: string): boolean {
    if (row.fileSize === -1) return false;
    if (!this.uiMetadata?.availableActions) return false;
    if (Array.isArray(this.uiMetadata.availableActions)) {
      if (!this.uiMetadata.availableActions.includes(action)) return false;
    } else if (typeof this.uiMetadata.availableActions === 'object' && this.uiMetadata.availableActions[action] !== true) {
      return false;
    }
    const canActionMap: { [key: string]: string } = {
      'edit': 'canEdit',
      'replace': 'canReplace',
      'archive': 'canArchive',
      'upload': 'canUpload'
    };
    const canProperty = canActionMap[action];
    return !(canProperty && (row as any)[canProperty] !== true);
  }

  openDocument(row: DocumentItem): void {
    const documentTypeId = (row as any)?.documentTypeId;
    const documentId = (row as any)?.edmRepositoryDocumentId;

    if (!row || ((documentTypeId === undefined || documentTypeId === null) && (documentId === undefined || documentId === null))) {
      this.eCaseSnackBarService.show(
        'failure',
        ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.downloadDocumentIdMissing')
      );
      return;
    }

    // Public downloads
    if (this.isPublic) {
      this.eCaseHttpService.get(this.loginAuthenticationService.baseUrl + '/api/downloadDocumentation/' + documentId, {
        observe: 'response',
        responseType: 'blob'
      }).subscribe({
        next: (response: any) => {
          ECaseUtils.generateHyperLinkForDownload(response);
        },
        error: (e) => {
          this.eCaseSnackBarService.show('failure', e);
        }
      });
      return;
    }

    // Live Application -- 999999999999000
    if (documentTypeId === SyntoDocumentListComponent.LIVE_DOC_ID_TEAM_MEMBER_CONSENT) {
      const formTypeId = row.formTypeId != null ? row.formTypeId : -1;
      window.open(
        this.loginAuthenticationService.baseUrl + '/api/generatePdfApplicationAdmin/' +
        this.entityId + '/' + row.attachedBySsoUserId + '/' + row.attachedByTeamMemberId + '/' + formTypeId,
        '_blank'
      );
      return;
    }

    // PA Report -- 999999999999006
    if (documentTypeId === SyntoDocumentListComponent.LIVE_DOC_ID_PA_REPORT) {
      window.open(
        this.loginAuthenticationService.baseUrl + '/api/generatePdfForAdminPaReport/' +
        this.entityId + '/' + row.attachedBySsoUserId,
        '_blank'
      );
      return;
    }

    // Application Revisions in Review -- 999999999999001
    if (documentTypeId === SyntoDocumentListComponent.LIVE_DOC_ID_APPLICATION_REVISIONS_IN_REVIEW) {
      window.open(
        this.loginAuthenticationService.baseUrl + '/api/generatePdfApplicationAdminRevision/' +
        this.entityId + '/' + row.attachedBySsoUserId,
        'blank'
      );
      return;
    }

    // APPLICATION REVIEWS (NORMAL) -- 999999999999002 / 999999999999003
    if ([
      SyntoDocumentListComponent.LIVE_DOC_ID_APPLICATION_REVIEWS,
      SyntoDocumentListComponent.LIVE_DOC_ID_APPLICATION_UNIQUE_REVIEWS
    ].includes(documentTypeId)) {
      window.open(
        this.loginAuthenticationService.baseUrl + '/api/generatePdfApplicationAdminReview/' +
        this.entityId + '/' + (row as any).contextObjectId,
        '_blank'
      );
      return;
    }

    // Reviews from Pre-Applications -- 999999999999004
    if (documentTypeId === SyntoDocumentListComponent.LIVE_DOC_ID_PRE_APPLICATION_REVIEWS) {
      const appId = (row as any).contextCaseApplicationId || this.entityId;
      window.open(
        this.loginAuthenticationService.baseUrl + '/api/generatePdfApplicationAdminReview/' +
        appId + '/' + (row as any).contextObjectId,
        '_blank'
      );
      return;
    }

    // Application Revisions at Different Steps -- 999999999999005
    if (documentTypeId === SyntoDocumentListComponent.LIVE_DOC_ID_APPLICATION_REVISIONS) {
      this.eCaseHttpService.post('/api/generateArchivedPdfApplicationAdmin', {
        attachedBySsoUserId: row.attachedBySsoUserId,
        attachedByTeamMemberId: row.attachedByTeamMemberId,
        formTypeId: row.formTypeId,
        documentName: row.fileName,
        applicationId: Number(this.entityId)
      }, {
        observe: 'response',
        responseType: 'blob'
      }).pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (_data: any) => {
            ECaseUtils.generateHyperLinkForDownload(_data);
          },
          error: (err) => {
            console.log(err);
            this.eCaseSnackBarService.show('failure', err.toString());
          }
        });
      return;
    }

    if (documentId === undefined || documentId === null) {
      this.eCaseSnackBarService.show(
        'failure',
        ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.downloadDocumentIdMissing')
      );
      return;
    }

    ECaseUtils.setKeyValueInTheSession(this.eCaseHttpService, 'documentId', documentId, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res.status) {
          this.eCaseHttpService.get('/api/reviewer/viewAttachedRepositoryDocumentSecured', {
            observe: 'response',
            responseType: 'blob'
          })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (_data: any) => {
                ECaseUtils.generateHyperLinkForDownload(_data);
              },
              error: (err) => {
                console.log(err);
                if (err.status === 500) {
                  this.eCaseSnackBarService.show(
                    'failure',
                    ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.downloadContentNotAvailable')
                  );
                } else {
                  const errorMessage = err?.errorMessage ||
                    err?.error?.errorMessage ||
                    err?.error?.message ||
                    err?.message ||
                    'ecase.explorer.documentList.downloadError';
                  this.eCaseSnackBarService.show(
                    'failure',
                    ECaseUtils.getTranslatedValueFromKey(this.translate, errorMessage)
                  );
                }
              }
            });
        } else {
          this.eCaseSnackBarService.show(
            'failure',
            ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.downloadSessionFailed')
          );
        }
      });
  }

  openDocumentApprovalPopUp(row: DocumentItem): void {
    const edmRepositoryDocumentId = (row as any)?.edmRepositoryDocumentId;
    const finEngagementDocId = (row as any)?.finEngagementDocId;
    const awfAwardFinalizationId = (row as any)?.awfAwardFinalizationId;
    const finBoardDecisionId = (row as any)?.finBoardDecisionId;

    let apiUrl: string;

    if (finBoardDecisionId && edmRepositoryDocumentId) {
      apiUrl = '/api/getApproversForFinancialDecisionDocument/' + edmRepositoryDocumentId + '/' + finBoardDecisionId;
    } else if (awfAwardFinalizationId && edmRepositoryDocumentId) {
      apiUrl = '/api/getApproversForAwfDocument/' + edmRepositoryDocumentId + '/' + awfAwardFinalizationId;
    } else if (finEngagementDocId && edmRepositoryDocumentId) {
      apiUrl = '/api/getApproversForFinEngagementDocument/' + edmRepositoryDocumentId + '/' + finEngagementDocId;
    } else {
      this.eCaseSnackBarService.show(
        'failure',
        ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.approvalInfoMissing')
      );
      return;
    }

    this.eCaseHttpService.get(apiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: any) => {
        if (response.status) {
          const dialogRef = this.dialog.open(DocumentApprovalDialogComponent, {
            width: '1000px'
          });
          const instance = dialogRef.componentInstance;
          instance.approvers = response.approvers.map((approver: any) => {
            approver.formattedApprovalDate = (approver.isApprovalProcessed === 1 && approver.approvalDate)
              ? this.eCaseDateFormatterPipe.transform(approver.approvalDate, this.syntoDateTime)
              : '';
            return approver;
          });
        } else {
          this.eCaseSnackBarService.show(
            'failure',
            ECaseUtils.getTranslatedValueFromKey(this.translate, response.errorMessage || 'ecase.explorer.documentList.approversLoadFailed')
          );
        }
      }, (error: any) => {
        const errorMessage = error?.errorMessage ||
          error?.error?.errorMessage ||
          error?.error?.message ||
          error?.message ||
          'ecase.explorer.documentList.approversLoadError';
        this.eCaseSnackBarService.show(
          'failure',
          ECaseUtils.getTranslatedValueFromKey(this.translate, errorMessage)
        );
      });
  }
}
